import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3, Users, Eye, Globe, TrendingUp, MousePointerClick, Calculator, ChevronDown, ChevronUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

type VisitLog = {
  id: string;
  ip_address: string;
  page: string;
  user_agent: string | null;
  referrer: string | null;
  created_at: string;
  city: string | null;
  country: string | null;
  org: string | null;
};

type CalcLog = {
  id: string;
  ip_address: string;
  folia: string | null;
  ksztalt: string | null;
  szerokosc: number | null;
  wysokosc: number | null;
  ilosc: number | null;
  cena_netto: number | null;
  created_at: string;
};

const StatCard = ({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub?: string }) => (
  <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-3">
    <div className="p-2 rounded-lg bg-accent/10">
      <Icon className="w-5 h-5 text-accent" />
    </div>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);

const AnalyticsDashboard = () => {
  const [visits, setVisits] = useState<VisitLog[]>([]);
  const [calcLogs, setCalcLogs] = useState<CalcLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllIPs, setShowAllIPs] = useState(false);
  const [showAllCalc, setShowAllCalc] = useState(false);
  const [manualIP, setManualIP] = useState("");
  const DEFAULT_HIDDEN_IPS = ["195.248.254.254", "31.0.37.7"];
  const [hiddenIPs, setHiddenIPs] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem("analytics_hidden_ips");
      if (saved) return new Set(JSON.parse(saved));
      // Set defaults on first load
      localStorage.setItem("analytics_hidden_ips", JSON.stringify(DEFAULT_HIDDEN_IPS));
      return new Set(DEFAULT_HIDDEN_IPS);
    } catch { return new Set(DEFAULT_HIDDEN_IPS); }
  });

  const toggleHideIP = (ip: string) => {
    setHiddenIPs(prev => {
      const next = new Set(prev);
      if (next.has(ip)) next.delete(ip); else next.add(ip);
      localStorage.setItem("analytics_hidden_ips", JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      const [visitsRes, calcRes] = await Promise.all([
        supabase.from("visit_logs").select("*").order("created_at", { ascending: false }).limit(1000),
        supabase.from("calculator_logs").select("*").order("created_at", { ascending: false }).limit(1000),
      ]);
      if (visitsRes.data) setVisits(visitsRes.data as unknown as VisitLog[]);
      if (calcRes.data) setCalcLogs(calcRes.data as unknown as CalcLog[]);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Auto-hide Lovable/Cloudflare infrastructure IPs
  const LOVABLE_IP_PREFIXES = ["104.28.", "44.247.", "34.141.", "35.187.", "35.198.", "35.205."];
  const isLovableIP = (ip: string) => LOVABLE_IP_PREFIXES.some(prefix => ip.startsWith(prefix));
  
  // Filter out hidden IPs and Lovable infrastructure IPs
  const filteredVisits = visits.filter(v => !hiddenIPs.has(v.ip_address) && !isLovableIP(v.ip_address));
  const filteredCalcLogs = calcLogs.filter(c => !hiddenIPs.has(c.ip_address) && !isLovableIP(c.ip_address));

  // Aggregate visits by day
  const dailyData = (() => {
    const map: Record<string, { visitors: Set<string>; pageviews: number }> = {};
    filteredVisits.forEach((v) => {
      const day = new Date(v.created_at).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" });
      if (!map[day]) map[day] = { visitors: new Set(), pageviews: 0 };
      map[day].visitors.add(v.ip_address);
      map[day].pageviews++;
    });
    return Object.entries(map)
      .map(([date, d]) => ({ date, visitors: d.visitors.size, pageviews: d.pageviews }))
      .reverse();
  })();

  // Unique IPs with visit count
  const ipStats = (() => {
    const map: Record<string, { count: number; pages: Set<string>; lastVisit: string; city: string | null; country: string | null; org: string | null }> = {};
    filteredVisits.forEach((v) => {
      if (!map[v.ip_address]) map[v.ip_address] = { count: 0, pages: new Set(), lastVisit: v.created_at, city: v.city, country: v.country, org: v.org };
      map[v.ip_address].count++;
      map[v.ip_address].pages.add(v.page);
      if (v.created_at > map[v.ip_address].lastVisit) map[v.ip_address].lastVisit = v.created_at;
      // Update geo if available from newer visit
      if (v.city) map[v.ip_address].city = v.city;
      if (v.country) map[v.ip_address].country = v.country;
      if (v.org) map[v.ip_address].org = v.org;
    });
    return Object.entries(map)
      .map(([ip, d]) => ({ ip, count: d.count, pages: d.pages.size, lastVisit: d.lastVisit, city: d.city, country: d.country, org: d.org }))
      .sort((a, b) => b.count - a.count);
  })();

  // Top pages
  const topPages = (() => {
    const map: Record<string, number> = {};
    filteredVisits.forEach((v) => {
    });
    return Object.entries(map)
      .map(([page, views]) => ({ page, views }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);
  })();

  // Calculator stats: most popular foils and quantities
  const calcStats = (() => {
    const foliaMap: Record<string, { count: number; totalQty: number; avgPrice: number; prices: number[] }> = {};
    filteredCalcLogs.forEach((c) => {
      const key = c.folia || "Nieznana";
      if (!foliaMap[key]) foliaMap[key] = { count: 0, totalQty: 0, avgPrice: 0, prices: [] };
      foliaMap[key].count++;
      foliaMap[key].totalQty += c.ilosc || 0;
      if (c.cena_netto) foliaMap[key].prices.push(c.cena_netto);
    });
    Object.values(foliaMap).forEach((f) => {
      f.avgPrice = f.prices.length > 0 ? f.prices.reduce((a, b) => a + b, 0) / f.prices.length : 0;
    });
    return Object.entries(foliaMap)
      .map(([folia, d]) => ({ folia, ...d }))
      .sort((a, b) => b.count - a.count);
  })();

  // Recent calculator logs
  const recentCalcLogs = filteredCalcLogs.slice(0, showAllCalc ? 50 : 10);

  const uniqueVisitors = new Set(filteredVisits.map((v) => v.ip_address)).size;
  const displayedIPs = showAllIPs ? ipStats : ipStats.slice(0, 15);

  if (loading) return <p className="text-muted-foreground text-sm">Ładowanie statystyk...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <BarChart3 className="w-5 h-5 text-accent" />
        <h2 className="text-lg font-bold text-foreground">Statystyki i ruch na stronie</h2>
        <span className="text-xs text-muted-foreground ml-auto">
          {filteredVisits.length} odsłon · {uniqueVisitors} unikalnych IP · {filteredCalcLogs.length} wycen
          {hiddenIPs.size > 0 && <span className="text-destructive ml-1">(ukryto {hiddenIPs.size} IP)</span>}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 items-center">
        {hiddenIPs.size > 0 && (
          <>
            <span className="text-xs text-muted-foreground">Ukryte IP:</span>
            {[...hiddenIPs].map(ip => (
              <button key={ip} onClick={() => toggleHideIP(ip)} className="text-[10px] bg-destructive/10 text-destructive px-2 py-0.5 rounded-full hover:bg-destructive/20 flex items-center gap-1">
                {ip} ✕
              </button>
            ))}
          </>
        )}
        <form onSubmit={(e) => {
          e.preventDefault();
          const trimmed = manualIP.trim();
          if (trimmed && /^\d{1,3}(\.\d{1,3}){3}$/.test(trimmed)) {
            toggleHideIP(trimmed);
            setManualIP("");
          }
        }} className="flex items-center gap-1 ml-auto">
          <input
            type="text"
            value={manualIP}
            onChange={(e) => setManualIP(e.target.value)}
            placeholder="Wpisz IP do ukrycia"
            className="text-xs bg-muted/30 border border-border rounded px-2 py-1 w-36 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button type="submit" className="text-xs bg-accent/10 text-accent px-2 py-1 rounded hover:bg-accent/20">
            Ukryj
          </button>
        </form>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Unikalne IP" value={String(uniqueVisitors)} sub="odwiedzających" />
        <StatCard icon={Eye} label="Odsłony" value={String(filteredVisits.length)} sub={`${uniqueVisitors > 0 ? (filteredVisits.length / uniqueVisitors).toFixed(1) : 0} na wizytę`} />
        <StatCard icon={Calculator} label="Wyceny" value={String(filteredCalcLogs.length)} sub="w kalkulatorze" />
        <StatCard icon={MousePointerClick} label="Popularne folie" value={String(calcStats.length)} sub="typów wycenianych" />
      </div>

      {/* Traffic Chart */}
      {dailyData.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-accent" /> Ruch dzienny
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <defs>
                  <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPageviews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    color: "hsl(var(--foreground))",
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="pageviews" name="Odsłony" stroke="hsl(var(--primary))" fill="url(#colorPageviews)" strokeWidth={2} />
                <Area type="monotone" dataKey="visitors" name="Unikalne IP" stroke="hsl(var(--accent))" fill="url(#colorVisitors)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* IP Addresses */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-accent" /> Adresy IP odwiedzających ({ipStats.length})
          </h3>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {displayedIPs.map((ip) => (
              <div key={ip.ip} className="flex flex-col gap-0.5 text-xs py-2 px-2 rounded-lg hover:bg-muted/30 group">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-foreground min-w-[120px]">{ip.ip}</span>
                  <span className="text-muted-foreground">{ip.count} odsłon</span>
                  <span className="text-muted-foreground">· {ip.pages} stron</span>
                  <button onClick={() => toggleHideIP(ip.ip)} className="text-[10px] text-destructive opacity-0 group-hover:opacity-100 transition-opacity ml-1" title="Ukryj to IP">
                    ukryj
                  </button>
                  <span className="text-muted-foreground ml-auto text-[10px]">
                    {new Date(ip.lastVisit).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                {(ip.city || ip.country || ip.org) && (
                  <div className="flex items-center gap-2 ml-[120px] text-[10px]">
                    {ip.city && <span className="text-accent font-medium">📍 {ip.city}</span>}
                    {ip.country && <span className="text-muted-foreground">🌍 {ip.country}</span>}
                    {ip.org && <span className="text-muted-foreground truncate max-w-[200px]" title={ip.org}>🏢 {ip.org}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
          {ipStats.length > 15 && (
            <button
              onClick={() => setShowAllIPs(!showAllIPs)}
              className="text-xs text-accent hover:underline mt-2 flex items-center gap-1"
            >
              {showAllIPs ? <><ChevronUp className="w-3 h-3" /> Pokaż mniej</> : <><ChevronDown className="w-3 h-3" /> Pokaż wszystkie ({ipStats.length})</>}
            </button>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4 text-accent" /> Najpopularniejsze strony
          </h3>
          <div className="space-y-2">
            {topPages.map((p) => {
              const maxViews = topPages[0]?.views || 1;
              return (
                <div key={p.page} className="flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-foreground truncate text-xs font-medium">{p.page}</span>
                      <span className="text-muted-foreground text-xs ml-2 shrink-0">{p.views}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-accent rounded-full" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
            {topPages.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Brak danych</p>}
          </div>
        </div>
      </div>

      {/* Calculator Usage */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Calculator className="w-4 h-4 text-accent" /> Wyceny w kalkulatorze
        </h3>

        {/* Most popular foils */}
        {calcStats.length > 0 && (
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Najpopularniejsze folie</p>
            <div className="space-y-1.5">
              {calcStats.slice(0, 10).map((s) => (
                <div key={s.folia} className="flex items-center gap-3 text-xs py-1.5 px-2 rounded-lg bg-muted/20">
                  <span className="text-foreground flex-1 min-w-0 truncate">{s.folia}</span>
                  <span className="text-accent font-bold shrink-0">{s.count}×</span>
                  <span className="text-muted-foreground shrink-0">Σ {s.totalQty} szt.</span>
                  {s.avgPrice > 0 && <span className="text-muted-foreground shrink-0">śr. {s.avgPrice.toFixed(0)} zł</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent calculator logs */}
        {filteredCalcLogs.length > 0 && (
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Ostatnie wyceny</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b border-border">
                    <th className="text-left py-1.5 px-2">Data</th>
                    <th className="text-left py-1.5 px-2">IP</th>
                    <th className="text-left py-1.5 px-2">Folia</th>
                    <th className="text-left py-1.5 px-2">Kształt</th>
                    <th className="text-right py-1.5 px-2">Rozmiar</th>
                    <th className="text-right py-1.5 px-2">Ilość</th>
                    <th className="text-right py-1.5 px-2">Cena netto</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalcLogs.map((log) => (
                    <tr key={log.id} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">
                        {new Date(log.created_at).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-foreground">{log.ip_address}</td>
                      <td className="py-1.5 px-2 text-foreground max-w-[200px] truncate">{log.folia || "—"}</td>
                      <td className="py-1.5 px-2 text-foreground">{log.ksztalt || "—"}</td>
                      <td className="py-1.5 px-2 text-right text-foreground">
                        {log.szerokosc && log.wysokosc ? `${log.szerokosc}×${log.wysokosc}` : "—"}
                      </td>
                      <td className="py-1.5 px-2 text-right text-foreground font-medium">{log.ilosc || "—"}</td>
                      <td className="py-1.5 px-2 text-right text-accent font-bold">
                        {log.cena_netto ? `${Number(log.cena_netto).toFixed(2)} zł` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCalcLogs.length > 10 && (
              <button
                onClick={() => setShowAllCalc(!showAllCalc)}
                className="text-xs text-accent hover:underline mt-2 flex items-center gap-1"
              >
                {showAllCalc ? <><ChevronUp className="w-3 h-3" /> Pokaż mniej</> : <><ChevronDown className="w-3 h-3" /> Pokaż więcej ({filteredCalcLogs.length})</>}
              </button>
            )}
          </div>
        )}

        {filteredCalcLogs.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">Brak wycen. Dane pojawią się gdy ktoś użyje kalkulatora.</p>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
